// Release packets (S12, D17): the Recruiter's release, restated before it happens. The dialog names who receives the
// one panel email (interviewers, HM, Recruiter and HRBP, every one a To recipient), what each recipient may open
// (their own packet link only) and what the email carries (the nine inclusions of the kickoff email), with the drafted
// message the release sends. The body is pure: the caller owns the reads and the writes, so the dialog renders the
// same under SSR and in the page. After the release it says that one released version creates one email.

import { useId } from "react";
import {
  ACTION_LABELS, Avatars, Banner, Button, CheckRow, Checklist, Dialog, DOCUMENT_LABELS, DraftedMessage, G7_TEMPLATES,
  KeyValue, ROLE_LABELS, ROOTS_LABELS, Skeleton, type Blocked, type Hs,
} from "@/mc";
import { KICKOFF_LABELS } from "./labels";

export interface ReleaseRecipient {
  id: string;
  name: string;
}

export interface ReleaseDialogProps {
  open: boolean;
  onClose: () => void;
  /** The release itself (packet.release). Absent: the dialog only restates (a seat that may not release). */
  onRelease?: (() => void) | undefined;
  busy?: boolean | undefined;
  /** The first open coverage row, when release is still blocked. */
  blocked?: Blocked | undefined;
  /** The server's refusal after the act, his rule sentence. */
  refused?: string | undefined;
  /** Everyone the panel email goes to, in order. */
  recipients: readonly ReleaseRecipient[];
  /** The drafted email (subject, then the body) exactly as the server composed it, or undefined while it loads. */
  draft?: { subject: string; body: string } | undefined;
  /** Draft the email again from the current panel. */
  onRegenerate?: (() => void) | undefined;
  regenerating?: boolean | undefined;
  /** Set once released; `version` is the released email's version (the email's own, never the release record's
   * concurrency version), absent until the read that carries the email has landed. */
  released?: { version?: number | undefined } | null | undefined;
  /** A gallery or test rendering: in place, inert. */
  preview?: boolean | undefined;
  hs?: Hs | undefined;
}

/** The nine inclusions of the kickoff email (D5), in the order he lists them. */
export const KICKOFF_INCLUSIONS = [
  ["app_access", KICKOFF_LABELS.appAccess],
  ["assignments", KICKOFF_LABELS.assignedValuesSkills],
  ["roots", ROOTS_LABELS.roots],
  ["skills", KICKOFF_LABELS.skills],
  ["packet_links", KICKOFF_LABELS.packetLinks],
  ["guidance", KICKOFF_LABELS.interviewGuidance],
  ["scorecard_sla", KICKOFF_LABELS.scorecardSla],
  ["debrief", KICKOFF_LABELS.debriefExpectations],
  ["confidentiality", KICKOFF_LABELS.confidentiality],
] as const;

export function ReleaseDialog({
  open, onClose, onRelease, busy, blocked, refused, recipients, draft, onRegenerate, regenerating, released, preview, hs,
}: ReleaseDialogProps) {
  const base = useId();
  const accessId = `${base}-access`;
  const emailId = `${base}-email`;
  const done = !!released;
  return (
    <Dialog
      preset="releasePackets"
      open={open}
      onClose={() => onClose()}
      preview={preview}
      dismissLabel={ACTION_LABELS.cancel}
      primary={onRelease && !done ? { label: ACTION_LABELS.release, onClick: onRelease, busy, blocked, hs: "pkt.release-confirm" } : undefined}
      refused={refused ? { reason: refused } : undefined}
      hs={hs}
    >
      <div className="mc-kp-dialog">
        {done ? (
          <Banner tone="ok" live>
            {DOCUMENT_LABELS.oneEmail}{released.version === undefined ? "" : ` ${G7_TEMPLATES.version({ n: released.version })}`}
          </Banner>
        ) : null}
        <section className="mc-kp-dialog-part">
          <p>{KICKOFF_LABELS.panelEmailTo}</p>
          {recipients.length > 0 ? <Avatars items={recipients.map((r) => ({ name: r.name, hueKey: r.id }))} max={8} /> : null}
        </section>
        <section className="mc-kp-dialog-part" aria-labelledby={accessId}>
          <h3 id={accessId} className="mc-kp-h3">{KICKOFF_LABELS.panelAccess}</h3>
          <KeyValue
            rows
            cols={1}
            items={[
              [KICKOFF_LABELS.packetLink, KICKOFF_LABELS.uniquePacketLinks],
              [ROLE_LABELS.interviewer, KICKOFF_LABELS.interviewerOwnPacket],
            ]}
          />
        </section>
        <section className="mc-kp-dialog-part" aria-labelledby={emailId}>
          <div className="mc-kp-dialog-head">
            <h3 id={emailId} className="mc-kp-h3">{KICKOFF_LABELS.kickoffEmail}</h3>
            {onRegenerate && !done ? (
              <Button tone="ghost" sm icon="refresh" busy={regenerating} onClick={onRegenerate} >
                {ACTION_LABELS.regenerate}
              </Button>
            ) : null}
          </div>
          <Checklist labelledBy={emailId}>
            {KICKOFF_INCLUSIONS.map(([key, word]) => <CheckRow key={key} status="ok">{word}</CheckRow>)}
          </Checklist>
          {draft ? <DraftedMessage>{`${draft.subject}\n\n${draft.body}`}</DraftedMessage> : <Skeleton lines={4} />}
        </section>
      </div>
    </Dialog>
  );
}
